import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AgentLayout from '../../components/agent/AgentLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'

interface Property {
    _id: string; title: string; price: number; city: string; area: string;
    status: string; type: string; images: string[]; isActive: boolean;
    isFeatured: boolean; createdAt: string;
}

export default function AgentMyProperty() {
    const { token } = useAuth()
    const [properties, setProperties] = useState<Property[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [msg, setMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null)

    useEffect(() => {
        if (!token) { setLoading(false); return }
        api.getMyProperties(token)
            .then(data => { if (Array.isArray(data)) setProperties(data) })
            .catch(console.error)
            .finally(() => setLoading(false))
    }, [token])

    const handleDelete = async (id: string, title: string) => {
        if (!token || !window.confirm(`Delete property "${title}"?`)) return
        setDeleting(id)
        const res = await api.deleteProperty(token, id)
        if (res.message === 'Property deleted successfully') {
            setProperties(p => p.filter(x => x._id !== id))
            setMsg({ type: 'success', text: 'Listing deleted successfully.' })
        } else {
            setMsg({ type: 'error', text: res.message || 'Delete failed.' })
        }
        setDeleting(null)
    }

    return (
        <AgentLayout>
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div>
                    <h3 className="fw-bold mb-1">My Property Listings</h3>
                    <p className="text-muted mb-0">Manage and monitor all your active real estate listings.</p>
                </div>
                <div>
                    <Link to="/agent-add-property" className="btn btn-success d-none d-md-inline-flex align-items-center gap-2 shadow-sm px-4">
                        <i className="fa-solid fa-plus"></i> Add New Listing
                    </Link>
                </div>
            </div>

            {msg && (
                <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'} border-0 shadow-sm mb-4`}>
                    {msg.text}
                </div>
            )}

            <div className="agent-table-container shadow-sm">
                <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 fw-bold text-dark">Your Listings ({properties.length})</h5>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-success" role="status"></div>
                        <p className="mt-2 text-muted">Fetching your listings...</p>
                    </div>
                ) : properties.length === 0 ? (
                    <div className="text-center py-5 bg-white">
                        <i className="fa-solid fa-house-circle-exclamation fa-4x mb-3 text-success opacity-50"></i>
                        <h4 className="fw-bold text-dark">No Listings Found</h4>
                        <p className="text-muted mb-4">Ready to sell something? Create your first listing now!</p>
                        <Link to="/agent-add-property" className="btn btn-success px-4 shadow-sm">
                            <i className="fa-solid fa-plus me-1"></i> Add Property
                        </Link>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="table agent-table m-0">
                            <thead className="bg-light">
                                <tr>
                                    <th>Property</th>
                                    <th>Pricing</th>
                                    <th>Status</th>
                                    <th>Location</th>
                                    <th>Added</th>
                                    <th className="text-end">Options</th>
                                </tr>
                            </thead>
                            <tbody>
                                {properties.map(p => (
                                    <tr key={p._id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="flex-shrink-0">
                                                    {p.images?.length > 0 ? (
                                                        <img src={p.images[0]} className="rounded shadow-sm" style={{ width: 60, height: 45, objectFit: 'cover' }} alt={p.title} />
                                                    ) : (
                                                        <div className="bg-light rounded text-center d-flex align-items-center justify-content-center" style={{ width: 60, height: 45 }}>
                                                            <i className="fa-solid fa-image text-muted small"></i>
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="fw-bold text-dark">{p.title}</div>
                                                    <small className="text-muted">{p.type}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="fw-bold text-success">₹{p.price?.toLocaleString()}</div>
                                        </td>
                                        <td>
                                            <span className={`badge px-2 py-1 rounded-pill`} style={{
                                                background: p.status === 'For Rent' ? '#eff6ff' : '#ecfdf5',
                                                color: p.status === 'For Rent' ? '#3b82f6' : '#10b981',
                                                border: `1px solid ${p.status === 'For Rent' ? '#3b82f615' : '#10b98115'}`
                                            }}>
                                                {p.status}
                                            </span>
                                        </td>
                                        <td><span className="text-dark small"><i className="fa-solid fa-location-dot me-1 text-muted"></i>{p.city}</span></td>
                                        <td><small className="text-muted">{new Date(p.createdAt).toLocaleDateString('en-IN')}</small></td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-2">
                                                <Link to={`/property-details/${p._id}`} className="btn btn-sm btn-light border" title="View">
                                                    <i className="fa-solid fa-eye text-primary"></i>
                                                </Link>
                                                <button className="btn btn-sm btn-light border text-danger"
                                                    disabled={deleting === p._id}
                                                    onClick={() => handleDelete(p._id, p.title)}
                                                    title="Delete"
                                                >
                                                    {deleting === p._id
                                                        ? <span className="spinner-border spinner-border-sm"></span>
                                                        : <i className="fa-solid fa-trash-can"></i>
                                                    }
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AgentLayout>
    )
}
